// TimeSheetPage.tsx - V7 (Optimized with Native Input + Branch Access Control)
// تحسينات: Debouncing + React.memo + Promise.all + Map + Native HTML inputs + Branch Filtering
import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Paper,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  EventAvailable as EventAvailableIcon,
  WorkOff as WorkOffIcon,
  EventBusy as EventBusyIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  DeleteSweep as ClearIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useAppSelector } from '../hooks/redux.hooks';
import axiosInstance from '../services/axiosInstance';
import timeSheetsService from '../services/timesheets.service';
import { branchesService } from '../services/branches.service';
import employeesService from '../services/employees.service';
import { leavesService, BlockedDate } from '../services/leaves.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { Branch } from '../types/branches.types';
import { CreateTimeSheetDto } from '../types/timesheets.types';
import { toast } from 'react-toastify';

const translations = {
  en: {
    title: 'TimeSheet',
    subtitle: 'Manage employee work hours and attendance',
    branch: 'Branch',
    period: 'Period',
    from: 'From',
    to: 'To',
    loadData: 'Load Data',
    save: 'Save All',
    regularHours: 'Regular',
    overtimeHours: 'Overtime',
    offHours: 'Off',
    publicHolidayHours: 'Holiday',
    employeeCode: 'Code',
    employeeName: 'Employee Name',
    totalHours: 'Total Hours',
    loading: 'Loading...',
    noEmployees: 'No employees found',
    saveSuccess: 'TimeSheet saved successfully',
    saveError: 'Failed to save TimeSheet',
    loadError: 'Failed to load data',
    allBranches: 'All Branches',
    custom: 'Custom Period',
    period21to20: 'Period 21-20',
    fullMonth: 'Full Month',
    total: 'Total',
    fillFullMonth: 'Fill 8h'
  },
  ar: {
    title: 'سجل الحضور الشهري',
    subtitle: 'إدارة ساعات العمل والحضور للموظفين',
    branch: 'الفرع',
    period: 'الفترة',
    from: 'من',
    to: 'إلى',
    loadData: 'تحميل البيانات',
    save: 'حفظ الكل',
    regularHours: 'عادي',
    overtimeHours: 'إضافي',
    offHours: 'راحة',
    publicHolidayHours: 'عطلة رسمية',
    employeeCode: 'الكود',
    employeeName: 'اسم الموظف',
    totalHours: 'إجمالي الساعات',
    loading: 'جاري التحميل...',
    noEmployees: 'لا يوجد موظفين',
    saveSuccess: 'تم حفظ سجل الحضور بنجاح',
    saveError: 'فشل حفظ سجل الحضور',
    loadError: 'فشل تحميل البيانات',
    allBranches: 'جميع الفروع',
    custom: 'فترة مخصصة',
    period21to20: 'فترة 21-20',
    fullMonth: 'شهر كامل',
    total: 'الإجمالي',
    fillFullMonth: 'تعبئة 8س'
  }
};

interface DayData {
  date: string;
  regularHours: number;
  overtimeHours: number;
  offHours: number;
  publicHolidayHours: number;
}

interface EmployeeTimeSheetData {
  employeeId: number;
  employeeCode: string;
  fullName: string;
  fullNameAr: string;
  branchId: number;
  days: Record<string, DayData>;
}

// مكون Input محسّن مع debouncing - باستخدام input HTML عادي
const HourInput = memo<{
  value: number;
  onChange: (value: number) => void;
  fieldType: string;
  maxHours?: number;
  disabled?: boolean;
}>(({ value, onChange, fieldType, maxHours = 24, disabled = false }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const clamp = (v: number) => Math.max(0, Math.min(v, maxHours));

  const handleChange = (newValue: string) => {
    let numValue = clamp(parseFloat(newValue) || 0);
    setLocalValue(numValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onChange(numValue);
    }, 300);
  };

  const focusAndSelect = (el: HTMLInputElement) => {
    el.focus();
    el.select();
  };

  return (
    <input
      type="number"
      value={localValue}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
          // Clamp on leave
          const clamped = clamp(localValue);
          if (clamped !== localValue) { setLocalValue(clamped); onChange(clamped); }
          if (e.key === 'Enter') {
            e.preventDefault();
            const allInputs = document.querySelectorAll('input[type="number"]:not(:disabled)');
            const currentIndex = Array.from(allInputs).indexOf(e.currentTarget);
            if (currentIndex > -1 && currentIndex < allInputs.length - 1) {
              focusAndSelect(allInputs[currentIndex + 1] as HTMLInputElement);
            }
          }
        }
      }}
      min={0}
      max={maxHours}
      step={0.5}
      style={{
        width: '100%',
        padding: '8.5px 14px',
        fontSize: '0.875rem',
        textAlign: 'center',
        border: disabled ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '6px',
        outline: 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        fontFamily: 'inherit',
        backgroundColor: disabled ? 'rgba(0,0,0,0.4)' : 'rgba(0, 0, 0, 0.2)',
        boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 -1px 2px rgba(255, 255, 255, 0.05)',
        color: disabled ? 'rgba(255,255,255,0.3)' : 'inherit',
        fontWeight: '500',
        cursor: disabled ? 'not-allowed' : 'text',
        opacity: disabled ? 0.5 : 1,
      }}
      onFocus={(e) => {
        if (disabled) return;
        e.target.select();
        e.target.style.borderColor = '#42a5f5';
        e.target.style.borderWidth = '2px';
        e.target.style.padding = '7.5px 13px';
        e.target.style.backgroundColor = 'rgba(66, 165, 245, 0.15)';
        e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(66, 165, 245, 0.25), 0 4px 12px rgba(66, 165, 245, 0.3)';
        e.target.style.transform = 'scale(1.05)';
        e.target.style.fontWeight = '600';
        e.target.style.zIndex = '10';
        e.target.style.position = 'relative';
      }}
      onBlur={(e) => {
        // Clamp value on blur
        const clamped = clamp(parseFloat(e.target.value) || 0);
        if (clamped !== localValue) { setLocalValue(clamped); onChange(clamped); }
        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
        e.target.style.borderWidth = '1px';
        e.target.style.padding = '8.5px 14px';
        e.target.style.backgroundColor = disabled ? 'rgba(0,0,0,0.4)' : 'rgba(0, 0, 0, 0.2)';
        e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 -1px 2px rgba(255, 255, 255, 0.05)';
        e.target.style.transform = 'scale(1)';
        e.target.style.fontWeight = '500';
        e.target.style.zIndex = '1';
      }}
    />
  );
});

HourInput.displayName = 'HourInput';

const getLeaveIcon = (leaveType: string): { icon: string; color: string } => {
  switch (leaveType) {
    case 'Annual': return { icon: '✈', color: '#1565c0' };
    case 'Hajj': return { icon: '🕋', color: '#1b5e20' };
    case 'Umrah': return { icon: '🕌', color: '#2e7d32' };
    case 'Sick': return { icon: '✚', color: '#c62828' };
    case 'Maternity': return { icon: '👶', color: '#e91e63' };
    case 'Unpaid': return { icon: '⊘', color: '#e65100' };
    case 'Emergency': return { icon: '⚡', color: '#f9a825' };
    case 'Bereavement': case 'Iddah': return { icon: '🕊', color: '#616161' };
    default: return { icon: '✈', color: '#1565c0' };
  }
};

// مكون صف الموظف
const EmployeeRow = memo<{
  employee: EmployeeTimeSheetData;
  dates: string[];
  onUpdate: (employeeId: number, date: string, field: keyof DayData, value: number) => void;
  isRTL: boolean;
  t: any;
  blockedDates?: BlockedDate[];
  onPrint?: (employee: EmployeeTimeSheetData) => void;
  getMaxRegularHours?: (date: string) => number;
  maxOvertimePerDay?: number;
  locked?: boolean;
}>(({ employee, dates, onUpdate, isRTL, t, blockedDates = [], onPrint, getMaxRegularHours, maxOvertimePerDay = 10, locked = false }) => {
  const calculateTotal = (field: keyof DayData): number => {
    return Object.values(employee.days).reduce((sum, day) => sum + (Number(day[field]) || 0), 0);
  };

  // Build a quick lookup: date string -> BlockedDate
  const blockedMap = new Map<string, BlockedDate>();
  blockedDates.forEach(b => {
    const dateKey = b.date.split('T')[0];
    blockedMap.set(dateKey, b);
  });

  const isBlocked = (date: string) => blockedMap.has(date);
  const getBlockedInfo = (date: string) => blockedMap.get(date);

  const fillFullMonth = () => {
    let skipped = 0;
    dates.forEach(date => {
      if (isBlocked(date)) {
        skipped++;
        return;
      }
      const maxH = getMaxRegularHours ? getMaxRegularHours(date) : 8;
      onUpdate(employee.employeeId, date, 'regularHours', maxH);
    });
    if (skipped > 0) {
      toast.warning(isRTL ? `تم تخطي ${skipped} يوم (إجازة/غياب)` : `Skipped ${skipped} day(s) (leave/absence)`);
    }
  };

  const clearFullMonth = () => {
    dates.forEach(date => {
      if (isBlocked(date)) return;
      onUpdate(employee.employeeId, date, 'regularHours', 0);
      onUpdate(employee.employeeId, date, 'overtimeHours', 0);
      onUpdate(employee.employeeId, date, 'offHours', 0);
      onUpdate(employee.employeeId, date, 'publicHolidayHours', 0);
    });
    toast.success(isRTL ? 'تم مسح حضور الشهر' : 'Month attendance cleared');
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {isRTL ? employee.fullNameAr : employee.fullName} ({employee.employeeCode})
        </Typography>
        {onPrint && (
          <Tooltip title={isRTL ? 'طباعة' : 'Print'}>
            <IconButton size="small" onClick={() => onPrint(employee)} sx={{ p: 0.3 }}>
              <PrintIcon fontSize="small" sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      {/* Days Header for Each Employee */}
      <Box sx={{ display: 'flex', minWidth: 200 + (dates.length * 95) + 100, mb: 0.5, opacity: 0.7 }}>
        <Box sx={{ width: 200, px: 1 }}></Box>
        {dates.map(date => (
          <Box 
            key={`day-${date}`} 
            sx={{ 
              width: 95, 
              px: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: 500,
              color: 'text.secondary'
            }}
          >
            {new Date(date).getDate()}
          </Box>
        ))}
        <Box sx={{ width: 100 }}></Box>
      </Box>
      
      {/* Regular Hours */}
      <Box sx={{ display: 'flex', minWidth: 200 + (dates.length * 95) + 100, mb: 1 }}>
        <Box sx={{ width: 200, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
          {t.regularHours}
          {!locked && (
            <>
              <Tooltip title={t.fillFullMonth}>
                <IconButton size="small" onClick={fillFullMonth} sx={{ p: 0.3, color: '#4caf50' }}>
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={isRTL ? 'مسح الشهر' : 'Clear month'}>
                <IconButton size="small" onClick={clearFullMonth} sx={{ p: 0.3, color: '#f44336' }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        {dates.map(date => {
          const blocked = getBlockedInfo(date);
          return (
          <Box key={`reg-${date}`} sx={{ width: 95, px: 0, display: 'flex', justifyContent: 'center' }}>
            {blocked ? (
              <Tooltip title={`${blocked.reason === 'Leave' ? (isRTL ? 'إجازة' : 'Leave') : (isRTL ? 'غياب' : 'Absence')}: ${blocked.description}`}>
                <Box sx={{
                  width: 75, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 1, bgcolor: blocked.reason === 'Leave' ? getLeaveIcon(blocked.leaveType).color : '#c62828',
                  color: 'white', fontSize: '0.8rem', fontWeight: 700, cursor: 'not-allowed',
                }}>
                  {blocked.reason === 'Leave' ? getLeaveIcon(blocked.leaveType).icon : '✗'}
                </Box>
              </Tooltip>
            ) : (
              <HourInput
                value={employee.days[date]?.regularHours || 0}
                onChange={(val) => { const max = getMaxRegularHours ? getMaxRegularHours(date) : 8; onUpdate(employee.employeeId, date, 'regularHours', Math.min(val, max)); }}
                fieldType="regular"
                maxHours={getMaxRegularHours ? getMaxRegularHours(date) : 8}
                disabled={locked}
              />
            )}
          </Box>
          );
        })}
        <Box sx={{ width: 100, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
          {calculateTotal('regularHours').toFixed(1)}
        </Box>
      </Box>

      {/* Overtime Hours */}
      <Box sx={{ display: 'flex', minWidth: 200 + (dates.length * 95) + 100, mb: 1 }}>
        <Box sx={{ width: 200, px: 1, display: 'flex', alignItems: 'center' }}>
          <EventAvailableIcon sx={{ fontSize: 16, mr: 1, color: '#ff9800' }} />
          {t.overtimeHours}
        </Box>
        {dates.map(date => {
          const blocked = getBlockedInfo(date);
          return (
          <Box key={`ot-${date}`} sx={{ width: 95, px: 0, display: 'flex', justifyContent: 'center' }}>
            {blocked ? (
              <Box sx={{ width: 75, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, bgcolor: blocked.reason === 'Leave' ? getLeaveIcon(blocked.leaveType).color : '#c62828', color: 'white', fontSize: '0.7rem', fontWeight: 700, cursor: 'not-allowed', opacity: 0.6 }}>—</Box>
            ) : (
            <HourInput
              value={employee.days[date]?.overtimeHours || 0}
              onChange={(val) => onUpdate(employee.employeeId, date, 'overtimeHours', Math.min(val, maxOvertimePerDay))}
              fieldType="overtime"
              maxHours={maxOvertimePerDay}
              disabled={locked}
            />
            )}
          </Box>
          );
        })}
        <Box sx={{ width: 100, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
          {calculateTotal('overtimeHours').toFixed(1)}
        </Box>
      </Box>

      {/* Off Hours */}
      <Box sx={{ display: 'flex', minWidth: 200 + (dates.length * 95) + 100, mb: 1 }}>
        <Box sx={{ width: 200, px: 1, display: 'flex', alignItems: 'center' }}>
          <WorkOffIcon sx={{ fontSize: 16, mr: 1, color: '#9e9e9e' }} />
          {t.offHours}
        </Box>
        {dates.map(date => {
          const blocked = getBlockedInfo(date);
          return (
          <Box key={`off-${date}`} sx={{ width: 95, px: 0, display: 'flex', justifyContent: 'center' }}>
            {blocked ? (
              <Box sx={{ width: 75, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, bgcolor: blocked.reason === 'Leave' ? getLeaveIcon(blocked.leaveType).color : '#c62828', color: 'white', fontSize: '0.7rem', fontWeight: 700, cursor: 'not-allowed', opacity: 0.6 }}>—</Box>
            ) : (
            <HourInput
              value={employee.days[date]?.offHours || 0}
              onChange={(val) => onUpdate(employee.employeeId, date, 'offHours', val)}
              fieldType="off"
              disabled={locked}
            />
            )}
          </Box>
          );
        })}
        <Box sx={{ width: 100, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
          {calculateTotal('offHours').toFixed(1)}
        </Box>
      </Box>

      {/* Public Holiday Hours */}
      <Box sx={{ display: 'flex', minWidth: 200 + (dates.length * 95) + 100 }}>
        <Box sx={{ width: 200, px: 1, display: 'flex', alignItems: 'center' }}>
          <EventBusyIcon sx={{ fontSize: 16, mr: 1, color: '#f44336' }} />
          {t.publicHolidayHours}
        </Box>
        {dates.map(date => {
          const blocked = getBlockedInfo(date);
          return (
          <Box key={`ph-${date}`} sx={{ width: 95, px: 0, display: 'flex', justifyContent: 'center' }}>
            {blocked ? (
              <Box sx={{ width: 75, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, bgcolor: blocked.reason === 'Leave' ? getLeaveIcon(blocked.leaveType).color : '#c62828', color: 'white', fontSize: '0.7rem', fontWeight: 700, cursor: 'not-allowed', opacity: 0.6 }}>—</Box>
            ) : (
            <HourInput
              value={employee.days[date]?.publicHolidayHours || 0}
              onChange={(val) => onUpdate(employee.employeeId, date, 'publicHolidayHours', val)}
              fieldType="holiday"
              disabled={locked}
            />
            )}
          </Box>
          );
        })}
        <Box sx={{ width: 100, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
          {calculateTotal('publicHolidayHours').toFixed(1)}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
    </Box>
  );
});

EmployeeRow.displayName = 'EmployeeRow';

const TimeSheetPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  // Branch Access Control
  const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
  const canViewEmployee = (branchId: number) => {
    return userBranchIds.length === 0 || userBranchIds.includes(branchId);
  };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [periodType, setPeriodType] = useState<'21-20' | 'full' | 'custom'>('21-20');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [employees, setEmployees] = useState<EmployeeTimeSheetData[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [blockedDatesMap, setBlockedDatesMap] = useState<Record<number, BlockedDate[]>>({});
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [periodLocked, setPeriodLocked] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = user?.isSuperAdmin === true;
  const [hrSettings, setHrSettings] = useState({
    DailyWorkHours: '8', RamadanDailyWorkHours: '6', RamadanStartDate: '', RamadanEndDate: '', MaxOvertimeHoursPerDay: '10'
  });

  const getMaxRegularHours = useCallback((date: string) => {
    const d = new Date(date);
    const rStart = hrSettings.RamadanStartDate ? new Date(hrSettings.RamadanStartDate) : null;
    const rEnd = hrSettings.RamadanEndDate ? new Date(hrSettings.RamadanEndDate) : null;
    if (rStart && rEnd && d >= rStart && d <= rEnd) return parseInt(hrSettings.RamadanDailyWorkHours) || 6;
    return parseInt(hrSettings.DailyWorkHours) || 8;
  }, [hrSettings]);

  const maxOvertimePerDay = parseInt(hrSettings.MaxOvertimeHoursPerDay) || 10;

  useEffect(() => {
    loadBranches();
    initializeDates();
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    // Load HR settings
    axiosInstance.get('/SystemSettings/category/HR').then(res => {
      if (res.data) setHrSettings(prev => ({ ...prev, ...res.data }));
    }).catch(() => {});
  }, []);

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      toast.error(t.loadError);
    }
  };

  const initializeDates = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    const start = new Date(year, month, 21);
    const end = new Date(year, month + 1, 20);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const handlePeriodTypeChange = (type: '21-20' | 'full' | 'custom') => {
    setPeriodType(type);
    
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    if (type === '21-20') {
      const start = new Date(year, month, 21);
      const end = new Date(year, month + 1, 20);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    } else if (type === 'full') {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };

  const handleStartDateChange = (newStartDate: string) => {
    setStartDate(newStartDate);
    
    if (periodType === '21-20') {
      const start = new Date(newStartDate);
      const end = new Date(start);
      end.setMonth(start.getMonth() + 1);
      end.setDate(20);
      setEndDate(end.toISOString().split('T')[0]);
    } else if (periodType === 'full') {
      const start = new Date(newStartDate);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };

  const generateDateRange = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split('T')[0]);
    }
    
    return dates;
  };

  const loadEmployeesAndTimeSheets = async () => {
    if (!selectedBranch) {
      toast.error(isRTL ? 'الرجاء اختيار الفرع' : 'Please select a branch');
      return;
    }
    if (!startDate || !endDate) {
      toast.error(isRTL ? 'الرجاء تحديد التاريخ' : 'Please select dates');
      return;
    }

    try {
      setLoading(true);
      
      const [employeesResponse, timeSheets, blocked, payrollRes] = await Promise.all([
        employeesService.getForTimeSheet(selectedBranch?.branchId),
        timeSheetsService.getAll({
          branchId: selectedBranch?.branchId,
          startDate,
          endDate
        }),
        selectedBranch?.branchId
          ? leavesService.getBlockedDates(selectedBranch.branchId, startDate, endDate).catch(() => ({}))
          : Promise.resolve({}),
        axiosInstance.get('/Payrolls').catch(() => ({ data: [] }))
      ]);
      setBlockedDatesMap(blocked);

      // Check if any posted payroll overlaps with selected period
      const allPayrolls = (payrollRes.data || []) as any[];
      const isLocked = !isSuperAdmin && allPayrolls.some((p: any) => {
        if (p.status !== 'Posted') return false;
        if (selectedBranch?.branchId && p.branchId && p.branchId !== selectedBranch.branchId) return false;
        const pFrom = new Date(p.periodStart || p.fromDate).getTime();
        const pTo = new Date(p.periodEnd || p.toDate).getTime();
        const sFrom = new Date(startDate).getTime();
        const sTo = new Date(endDate).getTime();
        return pFrom <= sTo && pTo >= sFrom;
      });
      setPeriodLocked(isLocked);
      if (isLocked) {
        toast.warning(isRTL ? 'الفترة مقفلة — الرواتب تم ترحيلها. للقراءة فقط.' : 'Period is locked — payroll is posted. Read-only mode.');
      }

      const filteredEmployees = employeesResponse.data;

      const dateRange = generateDateRange(startDate, endDate);
      setDates(dateRange);

      // تحويل timeSheets لـ Map للبحث السريع O(1) بدلاً من O(n)
      const timeSheetsMap = new Map<string, any>();
      timeSheets.forEach(ts => {
        const key = `${ts.employeeId}_${ts.workDate.split('T')[0]}`;
        timeSheetsMap.set(key, ts);
      });

      const employeeData: EmployeeTimeSheetData[] = filteredEmployees.data
        .filter((emp: any) => canViewEmployee(emp.branchId))
        .map((emp: any) => {
          const daysRecord: Record<string, DayData> = {};
          
          dateRange.forEach(date => {
            const key = `${emp.employeeId}_${date}`;
            const existing = timeSheetsMap.get(key);

            daysRecord[date] = existing ? {
              date,
              regularHours: existing.regularHours,
              overtimeHours: existing.overtimeHours,
              offHours: existing.offHours,
              publicHolidayHours: existing.publicHolidayHours
            } : {
              date,
              regularHours: 0,
              overtimeHours: 0,
              offHours: 0,
              publicHolidayHours: 0
            };
          });

          return {
            employeeId: emp.employeeId,
            employeeCode: emp.employeeCode,
            fullName: emp.fullName,
            fullNameAr: emp.fullNameAr,
            branchId: emp.branchId || 0,
            days: daysRecord
          };
        });

      setEmployees(employeeData);
    } catch (error) {
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = useCallback((employeeId: number, date: string, field: keyof DayData, value: number) => {
    setEmployees(prev => prev.map(emp => {
      if (emp.employeeId === employeeId) {
        return {
          ...emp,
          days: {
            ...emp.days,
            [date]: {
              ...emp.days[date],
              [field]: value
            }
          }
        };
      }
      return emp;
    }));
  }, []);

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      const timeSheets: CreateTimeSheetDto[] = [];
      
      employees.forEach(emp => {
        Object.entries(emp.days).forEach(([date, dayData]) => {
          timeSheets.push({
            employeeId: emp.employeeId,
            branchId: emp.branchId || selectedBranch?.branchId || 0,
            workDate: date,
            regularHours: dayData.regularHours,
            overtimeHours: dayData.overtimeHours,
            offHours: dayData.offHours,
            publicHolidayHours: dayData.publicHolidayHours
          });
        });
      });

      await timeSheetsService.bulkCreateOrUpdate({ timeSheets });
      toast.success(t.saveSuccess);
      
    } catch (error) {
      toast.error(t.saveError);
    } finally {
      setSaving(false);
    }
  };

  const handlePrintEmployee = useCallback((employee: EmployeeTimeSheetData) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const totalReg = Object.values(employee.days).reduce((s, d) => s + (d.regularHours || 0), 0);
    const totalOT = Object.values(employee.days).reduce((s, d) => s + (d.overtimeHours || 0), 0);
    const totalOff = Object.values(employee.days).reduce((s, d) => s + (d.offHours || 0), 0);
    const totalPH = Object.values(employee.days).reduce((s, d) => s + (d.publicHolidayHours || 0), 0);
    const blocked = blockedDatesMap[employee.employeeId] || [];
    const blockedMap = new Map<string, BlockedDate>();
    blocked.forEach(b => blockedMap.set(b.date.split('T')[0], b));

    const logoHtml = companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}" style="height:60px;" />` : '';

    printWindow.document.write(`<!DOCTYPE html><html><head><title>TimeSheet - ${employee.fullName}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
  h2 { margin: 0; } .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
  table { border-collapse: collapse; width: 100%; } th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: center; }
  th { background: #f5f5f5; font-weight: 600; } .label-col { text-align: left; width: 100px; font-weight: 600; }
  .total-col { font-weight: 700; background: #e3f2fd; } .blocked { background: #ffebee; color: #c62828; font-weight: 700; }
  .leave-blocked { background: #e3f2fd; color: #1565c0; font-weight: 700; }
  .summary { margin-top: 15px; } .summary td { padding: 6px 12px; }
  @media print { body { margin: 10px; } }
</style></head><body>
<div class="header">
  <div>${logoHtml}<h2>${companyInfo?.companyName || 'Countora'}</h2></div>
  <div><h3>TimeSheet Report</h3><div>${employee.fullName} (${employee.employeeCode})</div>
  <div>${startDate} to ${endDate}</div></div>
</div>
<table><thead><tr><th class="label-col">Type</th>`);

    dates.forEach(date => {
      const d = new Date(date);
      const day = d.getDate();
      const dayName = dayNames[d.getDay()];
      const isFri = d.getDay() === 5;
      printWindow.document.write(`<th style="${isFri ? 'background:#fff3e0;' : ''}">${day}<br/><small>${dayName}</small></th>`);
    });
    printWindow.document.write(`<th class="total-col">Total</th></tr></thead><tbody>`);

    // Regular row
    printWindow.document.write(`<tr><td class="label-col">Regular</td>`);
    dates.forEach(date => {
      const b = blockedMap.get(date);
      if (b) {
        const cls = b.reason === 'Leave' ? 'leave-blocked' : 'blocked';
        printWindow.document.write(`<td class="${cls}">${b.reason === 'Leave' ? '✈' : '✗'}</td>`);
      } else {
        const v = employee.days[date]?.regularHours || 0;
        printWindow.document.write(`<td>${v || ''}</td>`);
      }
    });
    printWindow.document.write(`<td class="total-col">${totalReg}</td></tr>`);

    // Overtime row
    printWindow.document.write(`<tr><td class="label-col">Overtime</td>`);
    dates.forEach(date => {
      const v = employee.days[date]?.overtimeHours || 0;
      printWindow.document.write(`<td>${v || ''}</td>`);
    });
    printWindow.document.write(`<td class="total-col">${totalOT}</td></tr>`);

    // Off row
    printWindow.document.write(`<tr><td class="label-col">Off</td>`);
    dates.forEach(date => {
      const v = employee.days[date]?.offHours || 0;
      printWindow.document.write(`<td>${v || ''}</td>`);
    });
    printWindow.document.write(`<td class="total-col">${totalOff}</td></tr>`);

    // Holiday row
    printWindow.document.write(`<tr><td class="label-col">Holiday</td>`);
    dates.forEach(date => {
      const v = employee.days[date]?.publicHolidayHours || 0;
      printWindow.document.write(`<td>${v || ''}</td>`);
    });
    printWindow.document.write(`<td class="total-col">${totalPH}</td></tr></tbody></table>`);

    // Summary
    printWindow.document.write(`<table class="summary"><tr>
      <td><strong>Total Regular:</strong> ${totalReg}h</td>
      <td><strong>Overtime:</strong> ${totalOT}h</td>
      <td><strong>Off:</strong> ${totalOff}h</td>
      <td><strong>Holiday:</strong> ${totalPH}h</td>
      <td><strong>Grand Total:</strong> ${totalReg + totalOT + totalOff + totalPH}h</td>
    </tr></table>`);

    printWindow.document.write(`<div style="margin-top:40px;display:flex;justify-content:space-between;">
      <div>_______________<br/>Employee Signature</div>
      <div>_______________<br/>Manager Signature</div>
      <div>_______________<br/>HR Signature</div>
    </div></body></html>`);
    printWindow.document.close();
    printWindow.print();
  }, [dates, startDate, endDate, companyInfo, blockedDatesMap]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CalendarIcon sx={{ fontSize: 40 }} />
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, maxWidth: 900, overflow: 'hidden' }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Autocomplete
              options={branches}
              getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
              value={selectedBranch}
              onChange={(_, newValue) => setSelectedBranch(newValue)}
              renderInput={(params) => <TextField {...params} label={t.branch} />}
              sx={{ flex: 1 }}
            />
            
            <Stack direction="row" spacing={1}>
              <Button
                variant={periodType === '21-20' ? 'contained' : 'outlined'}
                onClick={() => handlePeriodTypeChange('21-20')}
              >
                21-20
              </Button>
              <Button
                variant={periodType === 'full' ? 'contained' : 'outlined'}
                onClick={() => handlePeriodTypeChange('full')}
              >
                {t.fullMonth}
              </Button>
              <Button
                variant={periodType === 'custom' ? 'contained' : 'outlined'}
                onClick={() => setPeriodType('custom')}
              >
                {t.custom}
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.from}
                value={startDate ? new Date(startDate) : null}
                onChange={(date) => handleStartDateChange(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={endDate ? new Date(endDate) : maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { sx: { flex: 1 } } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.to}
                value={endDate ? new Date(endDate) : null}
                onChange={(date) => {
                  const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setEndDate(newVal);
                  if (startDate && newVal && startDate > newVal) setStartDate('');
                }}
                format="dd/MM/yyyy"
                minDate={startDate ? new Date(startDate) : minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { sx: { flex: 1 } } }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadEmployeesAndTimeSheets}
              disabled={!selectedBranch}
              sx={{ minWidth: 150 }}
            >
              {t.loadData}
            </Button>
            {employees.length > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleSaveAll}
                disabled={saving || periodLocked}
                sx={{ minWidth: 150 }}
              >
                {periodLocked ? (isRTL ? 'الفترة مقفلة' : 'Period Locked') : saving ? t.loading : t.save}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* TimeSheet Grid */}
      {employees.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 2, overflow: 'auto' }}>

          <Box sx={{ overflowX: 'auto' }}>
            {/* Header Row */}
            <Box sx={{ display: 'flex', minWidth: 200 + (dates.length * 95) + 100, borderBottom: 2, borderColor: 'divider', pb: 1, mb: 2 }}>
              <Box sx={{ width: 200, fontWeight: 700, px: 1 }}>{t.employeeName}</Box>
              {dates.map(date => (
                <Box 
                  key={date} 
                  sx={{ 
                    width: 95, 
                    fontWeight: 600, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    px: 0
                  }}
                >
                  {new Date(date).getDate()}
                </Box>
              ))}
              <Box sx={{ width: 100, fontWeight: 700, px: 1, textAlign: 'center' }}>{t.total}</Box>
            </Box>

            {/* Employee Rows */}
            {employees.map(emp => (
              <EmployeeRow
                key={emp.employeeId}
                employee={emp}
                dates={dates}
                onUpdate={handleUpdate}
                isRTL={isRTL}
                t={t}
                blockedDates={blockedDatesMap[emp.employeeId] || []}
                onPrint={handlePrintEmployee}
                getMaxRegularHours={getMaxRegularHours}
                maxOvertimePerDay={maxOvertimePerDay}
                locked={periodLocked}
              />
            ))}
          </Box>
        </Paper>
      )}

      {employees.length === 0 && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <CalendarIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
          <Typography color="text.secondary">{t.noEmployees}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TimeSheetPage;